import React, { MouseEvent, useCallback, useMemo } from "react";

import cn from "classnames";
import { useIntl } from "react-intl";

import {
  AnyEntity,
  EntityTypesSupportingFieldCustomization,
  TypedEntity,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import Field from "./components/Field";
import registerAllComponents from "./components/registerAll";
import styles from "./FieldGrid.module.scss";
import { filterPinnedFields } from "./utils/util";

interface Props {
  className?: string;
  columns: IField[];
  entity: TypedEntity<AnyEntity, EntityTypesSupportingFieldCustomization>;
  limit?: number;
  showEmpty?: boolean;
}

const FieldGrid = ({ className, columns, entity, limit, showEmpty }: Props) => {
  const intl = useIntl();
  const [expanded, expand] = useBoolean(false);
  const filteredColumns = useMemo(() => columns.filter(filterPinnedFields), [columns]);

  const isLimited = limit !== undefined && !expanded && filteredColumns.length > limit;
  const visibleColumns = isLimited ? filteredColumns.slice(0, limit) : filteredColumns;

  const handleShowMore = useCallback(
    (e: MouseEvent) => {
      stopEvents(e);
      expand();
    },
    [expand]
  );

  if (!filteredColumns.length) {
    return null;
  }

  return (
    <div className={cn(styles.container, className)}>
      {visibleColumns.map((field) => (
        <Field entity={entity} field={field} key={field.name} showEmpty={showEmpty} />
      ))}
      {isLimited && (
        <Button className={styles.showMoreButton} onClick={handleShowMore} type="link">
          {intl.formatMessage(
            {
              id: "fieldGrid.showMore",
              defaultMessage: "+{count} more",
              description: "Button to show remaining hidden fields in the field grid",
            },
            { count: filteredColumns.length - limit }
          )}
        </Button>
      )}
    </div>
  );
};

registerAllComponents();

export default FieldGrid;
